import { css, type Theme } from '@emotion/react';
import { Item as RadixRadioGroupItem } from '@radix-ui/react-radio-group';
import { ListItemFigure } from 'client/components/Other/ListItemFigure';
import { Typography } from 'client/components/Typography';
import { applyEffect } from 'client/Theme';

export interface RadioGroupItemProps {
  id: string;
  title: string;
  subtitle?: string | JSX.Element;
  icon?: JSX.Element | null;
  image?: { alt: string; src: string } | null;
  ornament?: JSX.Element | null;
  disabled?: boolean;
}

export const RadioGroupItem = ({ id, title, subtitle, icon, image, ornament, disabled }: RadioGroupItemProps) => {
  return (
    <RadixRadioGroupItem value={id} disabled={disabled} css={itemStyle}>
      <ListItemFigure icon={icon} image={image} ornament={ornament} />
      <section>
        <Typography variant="body1" noWrap>
          {title}
        </Typography>
        {!!subtitle && (
          <Typography variant="body2" noWrap>
            {subtitle}
          </Typography>
        )}
      </section>
    </RadixRadioGroupItem>
  );
};

export const circleEmptyIconURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='192' height='192' ` +
  `viewBox='0 0 256 256'%3E%3Cpath fill='none' d='M0 0h256v256H0z'/%3E%3Ccircle cx='128' cy='128' r='96' fill='none' ` +
  `stroke='${theme.color.grey[80].replace('#', '%23')}' stroke-linecap='round' stroke-linejoin='round' stroke-width='24'/%3E%3C/svg%3E");`;

export const circleCheckIconURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='192' height='192' ` +
  `viewBox='0 0 256 256'%3E%3Cpath fill='none' d='M0 0h256v256H0z'/%3E%3Cpath d='M128 24a104 104 0 1 0 104 104A104.2 ` +
  `104.2 0 0 0 128 24Zm49.5 85.8-58.6 56a8.1 8.1 0 0 1-5.6 2.2 7.7 7.7 0 0 1-5.5-2.2l-29.3-28a8 8 0 1 1 11-11.6l23.8 ` +
  `22.7 53.2-50.7a8 8 0 0 1 11 11.6Z' fill='${theme.color.primary[100].replace('#', '%23')}' /%3E%3C/svg%3E");`;

export const circleDisabledIconURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='192' height='192' ` +
  `viewBox='0 0 256 256'%3E%3Cpath fill='none' d='M0 0h256v256H0z'/%3E%3Ccircle cx='128' cy='128' r='96' fill='none' ` +
  `stroke='${theme.color.grey[80].replace('#', '%23')}' stroke-width='24' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath ` +
  `fill='none' stroke='${theme.color.grey[80].replace('#', '%23')}' stroke-linecap='round' stroke-linejoin='round' stroke-width='24' ` +
  `d='m60 60 136 136'/%3E%3C/svg%3E");`;

export const itemStyle = (theme: Theme) => css`
  --background: ${theme.color.grey[60]};

  display: flex;
  position: relative;
  align-items: center;
  padding: 16px;
  min-height: 76px;
  background-color: var(--background);
  border: none;
  overflow: hidden;
  cursor: pointer;
  z-index: 1;

  > section > .body2 {
    display: flex;
    align-items: center;
    gap: 3px;
  }

  > figure > .ornament > svg:nth-child(2) {
    color: var(--background);
  }

  &::after {
    content: '';
    display: block;
    position: absolute;
    right: 16px;
    width: 24px;
    height: 24px;
    background-image: ${circleEmptyIconURI(theme)};
    background-size: 100%;
  }

  &:disabled {
    padding: 13px;
    background-color: transparent;
    border: 3px solid ${theme.color.grey[60]};
    cursor: not-allowed;

    &::after {
      right: 13px;
      background-image: ${circleDisabledIconURI(theme)};
    }

    > div,
    > section {
      opacity: 0.8;
    }

    > p > .tag {
      margin-right: 6px;
      background-color: ${theme.color.grey[80]};
      color: ${theme.color.white} !important;
    }
  }

  &:focus-visible {
    outline: solid 4px ${theme.color.primary[100]};
    outline-offset: 4px;
    z-index: 2;
  }

  &:hover:not(:disabled):not([data-state='checked']) {
    --background: ${applyEffect(theme.color.grey[60], 'hover')};
  }

  &[data-state='checked'] {
    --background: ${theme.color.primary[60]};

    &::before {
      content: '';
      position: absolute;
      display: block;
      left: -4px;
      width: 8px;
      height: calc(100% - 32px);
      border-radius: 8px;
      background-color: ${theme.color.primary[100]};
    }

    &::after {
      background-image: ${circleCheckIconURI(theme)};
    }
  }

  &:first-of-type {
    border-top-left-radius: ${theme.borderRadius}px;
    border-top-right-radius: ${theme.borderRadius}px;
  }

  &:last-of-type {
    border-bottom-left-radius: ${theme.borderRadius}px;
    border-bottom-right-radius: ${theme.borderRadius}px;
  }

  > section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-left: 16px;

    > .body1 {
      font-weight: bold;
      color: ${theme.color.foreground};
    }
  }
`;
